/**
 * Main menu and multiplayer lobby.
 *
 * The relay URL is entered at runtime rather than baked in at build time,
 * because the client is served from GitHub Pages while the relay lives on a
 * separate host. It persists in localStorage so it only has to be typed once.
 */

import { AI_PRESETS, type AiOptions } from '../game/ai.js';
import type { LobbyState } from '../net/ws.js';

const RELAY_KEY = 'rts.relayUrl';
const NAME_KEY = 'rts.playerName';

export interface MenuCallbacks {
  onSkirmish(ai: AiOptions): void;
  onConnect(url: string, room: string, name: string): void;
}

export class Menu {
  readonly root: HTMLElement;
  private lobbyBox: HTMLElement;
  private statusEl: HTMLElement;
  private readyBtn: HTMLButtonElement;
  private onReady: ((ready: boolean) => void) | null = null;
  private isReady = false;

  constructor(
    parent: HTMLElement,
    private cb: MenuCallbacks,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'menu';

    const panel = document.createElement('div');
    panel.className = 'menu-panel';

    const title = document.createElement('h1');
    title.textContent = 'RED HORIZON';
    const sub = document.createElement('p');
    sub.className = 'menu-sub';
    sub.textContent = 'Deterministic lockstep RTS — Rust/WASM simulation, WebGPU renderer';

    // Skirmish.
    const skirmish = document.createElement('section');
    skirmish.className = 'menu-section';
    const skirmishTitle = document.createElement('h2');
    skirmishTitle.textContent = 'Skirmish';
    const diffRow = document.createElement('div');
    diffRow.className = 'row';
    for (const [name, opts] of Object.entries(AI_PRESETS)) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'primary';
      btn.textContent = name.charAt(0).toUpperCase() + name.slice(1);
      btn.addEventListener('click', () => this.cb.onSkirmish(opts));
      diffRow.appendChild(btn);
    }
    skirmish.append(skirmishTitle, diffRow);

    // Multiplayer.
    const mp = document.createElement('section');
    mp.className = 'menu-section';
    const mpTitle = document.createElement('h2');
    mpTitle.textContent = 'Multiplayer';

    const nameInput = field('Your name', localStorage.getItem(NAME_KEY) ?? 'Commander');
    const relayInput = field('Relay server', localStorage.getItem(RELAY_KEY) ?? defaultRelay());
    const roomInput = field('Room', 'lobby');

    const connectBtn = document.createElement('button');
    connectBtn.type = 'button';
    connectBtn.className = 'primary wide';
    connectBtn.textContent = 'Connect';
    connectBtn.addEventListener('click', () => {
      const url = relayInput.input.value.trim();
      const room = roomInput.input.value.trim() || 'lobby';
      const name = nameInput.input.value.trim() || 'Commander';
      localStorage.setItem(RELAY_KEY, url);
      localStorage.setItem(NAME_KEY, name);
      this.cb.onConnect(url, room, name);
    });

    const hint = document.createElement('p');
    hint.className = 'hint';
    hint.textContent =
      'This page is served over HTTPS, so the relay must be wss:// (not ws://) or the browser will block the connection.';

    mp.append(mpTitle, nameInput.wrap, relayInput.wrap, roomInput.wrap, connectBtn, hint);

    // Lobby, hidden until connected.
    this.lobbyBox = document.createElement('section');
    this.lobbyBox.className = 'menu-section lobby hidden';
    this.statusEl = document.createElement('div');
    this.statusEl.className = 'lobby-status';
    this.readyBtn = document.createElement('button');
    this.readyBtn.type = 'button';
    this.readyBtn.className = 'primary wide';
    this.readyBtn.textContent = 'Ready';
    this.readyBtn.addEventListener('click', () => {
      this.isReady = !this.isReady;
      this.readyBtn.textContent = this.isReady ? 'Not ready' : 'Ready';
      this.onReady?.(this.isReady);
    });
    this.lobbyBox.append(this.statusEl, this.readyBtn);

    const controls = document.createElement('details');
    controls.className = 'controls';
    const summary = document.createElement('summary');
    summary.textContent = 'Controls';
    const list = document.createElement('div');
    list.className = 'controls-grid';
    for (const [k, v] of [
      ['Left drag', 'Select units'],
      ['Right click', 'Move / attack / harvest'],
      ['Alt + right click', 'Attack-move'],
      ['Shift + click', 'Add to selection'],
      ['S', 'Stop'],
      ['Ctrl + 1..9', 'Assign control group'],
      ['1..9', 'Recall control group'],
      ['Q W E', 'Infantry / Tank / Harvester'],
      ['R T Y', 'Refinery / Barracks / War Factory'],
      ['Alt (hold)', 'Show all health bars'],
      ['Wheel', 'Zoom'],
      ['Arrows / screen edge', 'Pan camera'],
    ]) {
      const dk = document.createElement('kbd');
      dk.textContent = k;
      const dv = document.createElement('span');
      dv.textContent = v;
      list.append(dk, dv);
    }
    controls.append(summary, list);

    panel.append(title, sub, skirmish, mp, this.lobbyBox, controls);
    this.root.appendChild(panel);
    parent.appendChild(this.root);
  }

  setLobbyHandler(fn: (ready: boolean) => void): void {
    this.onReady = fn;
  }

  showLobby(state: LobbyState): void {
    if (state.phase === 'lobby') {
      this.lobbyBox.classList.remove('hidden');
      const names = state.players
        .map((p, i) => `${p.ready ? '✔' : '•'} ${p.name}${i === state.playerIndex ? ' (you)' : ''}`)
        .join('\n');
      this.statusEl.textContent = `Room "${state.room}"\n${names}\n\nThe match starts when everyone is ready.`;
    } else if (state.phase === 'connecting') {
      this.lobbyBox.classList.remove('hidden');
      this.statusEl.textContent = 'Connecting...';
    } else if (state.phase === 'error') {
      this.lobbyBox.classList.remove('hidden');
      this.statusEl.textContent = state.message;
      this.isReady = false;
      this.readyBtn.textContent = 'Ready';
    }
  }

  hide(): void {
    this.root.classList.add('hidden');
  }

  show(): void {
    this.root.classList.remove('hidden');
    this.lobbyBox.classList.add('hidden');
    this.isReady = false;
    this.readyBtn.textContent = 'Ready';
  }
}

function field(label: string, value: string): { wrap: HTMLElement; input: HTMLInputElement } {
  const wrap = document.createElement('label');
  wrap.className = 'field';
  const span = document.createElement('span');
  span.textContent = label;
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value;
  wrap.append(span, input);
  return { wrap, input };
}

/**
 * Works out the relay URL from where the page itself was served.
 *
 * In production the client and the relay share one origin, with nginx routing
 * `/ws` to the relay, so the address can be derived rather than typed. It stays
 * editable because a local build may want to point at a remote relay, or the
 * reverse.
 */
function defaultRelay(): string {
  if (typeof location === 'undefined') return 'ws://localhost:8080';
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    // Dev: vite on 5173, relay on 8080, two separate processes.
    return 'ws://localhost:8080';
  }
  // Same origin, same scheme: https implies wss, which is what keeps the
  // browser from rejecting the socket as mixed content.
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${location.host}/ws`;
}
